import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';

import { RouteFacade } from '@mitsubishi/core';
import { Study } from '@mitsubishi/shared/data-access';

import { StudyService } from '../services/study.service';
import { ProjectFacade } from '../state/project/project.facade';
import { StudyFacade } from '../state/study/study.facade';
import { ProjectStudiesStore } from '../stores/project-studies.store';
import { isNewStudyPageEnabled } from '../utils/new-study-page';

/**
 * Loads the interim study before its page renders — through whichever endpoint the flag state's
 * readers are typed against.
 *
 * **Flag on**: `GET …/studies/{studyId}`, into `ProjectStudiesStore.detailById`. A temporary study is a
 * study (contract R11), so the paginated page finds it exactly where it looks for a permanent one, and
 * the legacy readers keep working because `StoreStudyReadSource` adapts every held detail into the
 * legacy shape. This is why the store needs no temporary-study slice of its own.
 *
 * **Flag off**: the legacy `getTemporaryStudyDetail`, into the NGXS project tree, as before.
 */
export const interimStudyResolver: ResolveFn<unknown> = () => {
  const routeFacade = inject(RouteFacade);
  const { projectId, interimStudyId, studyId } = routeFacade;

  if (!projectId || (!interimStudyId && !studyId)) {
    return undefined;
  }

  // If interimStudyId is available, use it; otherwise fall back to studyId
  const targetStudyId = interimStudyId || studyId;

  if (isNewStudyPageEnabled()) {
    return inject(ProjectStudiesStore)
      .ensureStudy(targetStudyId)
      .pipe(catchError(() => of(undefined)));
  }

  const studyFacade = inject(StudyFacade);
  const existingStudy = studyFacade.getStudyById(targetStudyId);
  if (existingStudy) {
    return existingStudy;
  }

  const projectFacade = inject(ProjectFacade);
  return inject(StudyService)
    .getTemporaryStudyDetail(projectId, targetStudyId)
    .pipe(
      tap((study: Study) => projectFacade.addStudyToProject(study)),
      catchError(() => of(undefined))
    );
};
